/**
 * PhotosGroupPicker — reusable component for selecting Photos group memberships.
 *
 * Displays available Photos groups with their collection access context.
 * Used in invite creation and request approval flows.
 *
 * Props:
 *   value: array of { group_id, assigned_level } selections
 *   onChange: called with updated array when selections change
 */

import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import './PhotosGroupPicker.css';

const ACCESS_LEVELS = [
  { value: 'view', label: 'View' },
  { value: 'view_upload', label: 'View + Upload' },
  { value: 'full_manage', label: 'Full Manage' },
];

export default function PhotosGroupPicker({ value = [], onChange }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadGroups();
  }, []);

  async function loadGroups() {
    try {
      const data = await api.get('/api/admin/photos/groups');
      setGroups(data);
    } catch (err) {
      setError('Could not load Photos groups');
      console.error('Failed to load Photos groups:', err);
    } finally {
      setLoading(false);
    }
  }

  function isSelected(groupId) {
    return value.some((v) => v.group_id === groupId);
  }

  function getLevel(groupId) {
    const match = value.find((v) => v.group_id === groupId);
    return match?.assigned_level || 'view';
  }

  function toggleGroup(groupId) {
    if (isSelected(groupId)) {
      onChange(value.filter((v) => v.group_id !== groupId));
    } else {
      onChange([...value, { group_id: groupId, assigned_level: 'view' }]);
    }
  }

  function setLevel(groupId, level) {
    onChange(
      value.map((v) =>
        v.group_id === groupId ? { ...v, assigned_level: level } : v
      )
    );
  }

  if (loading) {
    return <div className="photos-picker-loading">Loading Photos groups…</div>;
  }

  if (error) {
    return <div className="photos-picker-error">{error}</div>;
  }

  if (groups.length === 0) {
    return (
      <div className="photos-picker-empty">
        No groups configured in Nacho Photos yet.
      </div>
    );
  }

  return (
    <div className="photos-picker">
      <div className="photos-picker-label">Nacho Photos Access</div>
      <div className="photos-picker-help">
        Select which Photos groups this user should be added to.
      </div>
      <div className="photos-picker-groups">
        {groups.map((group) => (
          <div
            key={group.id}
            className={`photos-picker-group ${isSelected(group.id) ? 'selected' : ''}`}
          >
            <label className="photos-picker-group-header">
              <input
                type="checkbox"
                checked={isSelected(group.id)}
                onChange={() => toggleGroup(group.id)}
              />
              <span className="photos-picker-group-name">{group.name}</span>
              {group.description && (
                <span className="photos-picker-group-desc">{group.description}</span>
              )}
            </label>

            {isSelected(group.id) && (
              <div className="photos-picker-group-config">
                <select
                  value={getLevel(group.id)}
                  onChange={(e) => setLevel(group.id, e.target.value)}
                  className="photos-picker-level-select"
                >
                  {ACCESS_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>

                {group.collections.length > 0 && (
                  <div className="photos-picker-collections">
                    <span className="photos-picker-collections-label">
                      Access to:
                    </span>
                    {group.collections.map((col) => (
                      <span key={col.id} className="photos-picker-collection-tag">
                        {col.name}
                        <span className="photos-picker-collection-level">
                          {col.max_level}
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
